import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import SectionHeading from '@/components/base/SectionHeading';
import WorkCard from '@/components/base/WorkCard';
import NewsCard from '@/components/base/NewsCard';
import { useSeo } from '@/hooks/useSeo';
import { works } from '@/mocks/works';
import { news } from '@/mocks/news';

const sortedNews = [...news].sort((a, b) => (a.date < b.date ? 1 : -1));

export default function Home() {
  useSeo({
    title: '夏目彩春－最新影视资讯与作品介绍平台',
    description:
      '夏目彩春官网，提供最新影视资讯、人物简介及多部影视作品介绍。覆盖电视剧、电影内容，满足影迷全面了解夏目彩春的需求。',
    keywords: '夏目彩春,影视资讯,演员介绍,电视剧,电影作品,影迷平台',
  });

  const featured = sortedNews[0];
  const secondary = sortedNews.slice(1, 3);
  const ticker = sortedNews.slice(0, 6);
  const hotWorks = works.slice(0, 8);

  return (
    <>
      <HeroCarousel />

      {/* Latest news */}
      <section className="bg-background-50">
        <div className="w-full max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Latest News"
            title="最新影视资讯"
            description="紧跟夏目彩春的最新动态，第一时间了解作品进展、活动信息与行业报道。"
            moreTo="/news"
            moreLabel="全部资讯"
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 flex flex-col gap-5">
              {featured && <NewsCard item={featured} />}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {secondary.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="h-full bg-background-100 border border-background-200/80 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                    <i className="ri-flashlight-fill text-sm" />
                  </span>
                  <h3 className="font-heading font-bold text-base text-foreground-950">资讯快讯</h3>
                </div>
                <div className="flex flex-col">
                  {ticker.map((item) => (
                    <NewsCard key={item.id} item={item} variant="compact" />
                  ))}
                </div>
                <Link
                  to="/news"
                  className="mt-4 flex items-center justify-center gap-1.5 w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览更多资讯
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Hot works */}
      <section className="bg-background-100">
        <div className="w-full max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Featured Works"
            title="热门影视作品推荐"
            description="精选夏目彩春代表性影视作品，涵盖电影与电视剧多种题材。"
            moreTo="/category"
            moreLabel="全部分类"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {hotWorks.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </div>
      </section>

      {/* Site intro band */}
      <section className="relative overflow-hidden bg-primary-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20cinematic%20background%20of%20blurred%20film%20set%20lights%20and%20soft%20bokeh%2C%20deep%20navy%20and%20warm%20amber%20glow%2C%20minimal%20textured%20gradient%2C%20atmospheric%20moody%20lighting%2C%20elegant%20composition&width=1600&height=600&seq=natsume-intro-band&orientation=landscape"
            alt="影视氛围背景"
            title="夏目彩春影视平台"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative w-full max-w-content mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="font-label text-xs tracking-[0.25em] uppercase text-accent-400">About The Platform</span>
              <h2 className="font-heading font-black text-3xl md:text-4xl text-background-50 mt-3 leading-tight">
                关于夏目彩春影迷平台
              </h2>
              <p className="mt-5 text-sm md:text-base text-background-100/85 leading-relaxed">
                本站围绕演员夏目彩春，汇聚最新影视资讯、人物简介与多部影视作品介绍。无论你是刚认识她的新观众，还是关注多年的老影迷，都能在这里快速、全面地了解她的作品与动态。
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  了解演员简介
                </Link>
                <Link
                  to="/category"
                  className="px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  探索影视作品
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-film-line', value: '10+', label: '精选影视作品' },
                { icon: 'ri-newspaper-line', value: '8+', label: '最新资讯报道' },
                { icon: 'ri-award-line', value: '12', label: '获奖与提名' },
                { icon: 'ri-time-line', value: '10', label: '出道年数' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-50/10 border border-background-50/15 p-5">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50 mb-3">
                    <i className={`${stat.icon} text-base`} />
                  </span>
                  <div className="font-label text-2xl text-background-50">{stat.value}</div>
                  <div className="text-xs text-background-100/70 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}